import { analyzeCallRecordingService } from "../services/campaign.services";

const PEAK_BARS = 2400;

const peaksFromChannel = (data, bars) => {
  const peaks = new Array(bars);
  const block = Math.max(1, Math.floor(data.length / bars));
  let loudest = 0.01;
  for (let i = 0; i < bars; i++) {
    let max = 0;
    const start = i * block;
    const end = Math.min(data.length, start + block);
    for (let j = start; j < end; j += 8) {
      const value = Math.abs(data[j]);
      if (value > max) max = value;
    }
    peaks[i] = max;
    if (max > loudest) loudest = max;
  }
  return peaks.map((value) => value / loudest);
};

const rmsInRanges = (data, sampleRate, ranges) => {
  let sum = 0;
  let count = 0;
  ranges.forEach((range) => {
    const start = Math.max(0, Math.floor(range.start * sampleRate));
    const end = Math.min(data.length, Math.floor(range.end * sampleRate));
    for (let i = start; i < end; i += 16) {
      sum += data[i] * data[i];
      count += 1;
    }
  });
  return count ? Math.sqrt(sum / count) : 0;
};

export const WAVEFORM_LANE_COLORS = {
  caller: "#0EA5E9",
  bot: "#A3E635",
  mix: "#6366F1",
};

export const fetchRecordingBytes = async (audioUrl, callUuid) => {
  if (audioUrl) {
    try {
      const res = await fetch(audioUrl, { credentials: "include", mode: "cors" });
      if (res.ok) return await res.arrayBuffer();
    } catch {
      // CDN CORS — fall through to the authenticated API proxy.
    }
  }
  if (!callUuid) return null;
  const res = await analyzeCallRecordingService(callUuid);
  if (res?.status === 200 && res.data) return res.data;
  return null;
};

export const decodeAudioBuffer = async (bytes) => {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const context = new Ctx();
  return context.decodeAudioData(bytes.slice(0));
};

export const buildWaveform = (buffer, speechRanges = []) => {
  const channelCount = Math.min(buffer.numberOfChannels, 2);
  const peaks = [];
  for (let channel = 0; channel < channelCount; channel += 1) {
    peaks.push(peaksFromChannel(buffer.getChannelData(channel), PEAK_BARS));
  }
  if (channelCount === 1) {
    return {
      dual: false,
      lanes: [{ label: "Mix", peaks: peaks[0], color: WAVEFORM_LANE_COLORS.mix }],
    };
  }
  let botIndex = 1;
  if (speechRanges.length) {
    const energy0 = rmsInRanges(
      buffer.getChannelData(0),
      buffer.sampleRate,
      speechRanges
    );
    const energy1 = rmsInRanges(
      buffer.getChannelData(1),
      buffer.sampleRate,
      speechRanges
    );
    botIndex = energy1 >= energy0 ? 1 : 0;
  }
  const callerIndex = 1 - botIndex;
  return {
    dual: true,
    lanes: [
      { label: "Caller", peaks: peaks[callerIndex], color: WAVEFORM_LANE_COLORS.caller },
      { label: "Bot", peaks: peaks[botIndex], color: WAVEFORM_LANE_COLORS.bot },
    ],
  };
};

export const drawWaveform = (canvas, waveform, progress = 0) => {
  if (!canvas || !waveform?.lanes?.length) return;
  const ctx = canvas.getContext("2d");
  const width = canvas.width;
  const height = canvas.height;
  if (!width || !height) return;
  ctx.clearRect(0, 0, width, height);

  const laneHeight = height / waveform.lanes.length;
  const progressX = Math.max(0, Math.min(1, progress)) * width;

  waveform.lanes.forEach((lane, index) => {
    const top = index * laneHeight;
    const mid = top + laneHeight / 2;
    const amp = laneHeight / 2 - 6;
    const samples = lane.peaks;
    const barWidth = width / samples.length;

    ctx.strokeStyle = "rgba(24, 39, 75, 0.08)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, mid);
    ctx.lineTo(width, mid);
    ctx.stroke();

    for (let i = 0; i < samples.length; i += 1) {
      const x = i * barWidth;
      const mag = Math.max(samples[i] * amp, 0.6);
      ctx.fillStyle = x <= progressX ? lane.color : `${lane.color}99`;
      ctx.fillRect(x, mid - mag, Math.max(1, barWidth - 0.4), mag * 2);
    }
  });
};
